/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Button, Form, Input, Modal, notification } from 'antd';
import {
  MailOutlined,
  SendOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useMutation } from 'react-query';

// local imports
import { createPublicTenderSupplier } from '~src/services/api/shares';
import { formatDate } from '~src/utils/formatDate';

type PublicShareInviteModalProps = {
  visible: boolean;
  tenderShareId?: string;
  tenderData?: any;
  onClose: () => void;
};

type PublicShareInviteFormValues = {
  supplierName: string;
  contactEmail: string;
};

export const PublicShareInviteModal: React.FC<PublicShareInviteModalProps> = ({
  visible,
  tenderShareId,
  tenderData,
  onClose,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm<PublicShareInviteFormValues>();

  const [doCreatePublicTenderSupplier, { isLoading }] = useMutation(
    createPublicTenderSupplier,
    {
      onSuccess: () => {
        notification.success({
          message: intl.formatMessage({
            id: 'public-tender-supplier-invite-success-title',
          }),
          description: intl.formatMessage({
            id: 'public-tender-supplier-invite-success-description',
          }),
        });

        form.resetFields();
        onClose();
      },
      onError: () => {
        notification.error({
          message: intl.formatMessage({
            id: 'public-tender-supplier-invite-error-title',
          }),
          description: intl.formatMessage({
            id: 'public-tender-supplier-invite-error-description',
          }),
        });
      },
    }
  );

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (isLoading) return;

    form.resetFields();
    onClose();
  };

  const handleSubmit = async (values: PublicShareInviteFormValues) => {
    if (!tenderShareId) return;

    await doCreatePublicTenderSupplier({
      tenderShareId,
      supplier_name: values.supplierName.trim(),
      contact_email: values.contactEmail.trim(),
    });
  };

  const tenderTitle =
    tenderData?.share_title || tenderData?.title || tenderShareId || '-';

  const deadlineAt = Number(tenderData?.deadline_at) || 0;
  const activeUntil = Number(tenderData?.active_until) || 0;
  const tenderDeadline = activeUntil > 3601 ? activeUntil : deadlineAt;
  const globalSerial = tenderData?.global_serial;

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      centered
      destroyOnClose
      width={540}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <UserAddOutlined style={{ color: '#1890ff' }} rev={''} />
          {intl.formatMessage({
            id: 'public-tender-invite-supplier-modal-title',
          })}
        </div>
      }
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {intl.formatMessage({ id: 'close' })}
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<SendOutlined rev={''} />}
          loading={isLoading}
          disabled={!tenderShareId}
          onClick={() => form.submit()}>
          {intl.formatMessage({
            id: 'public-tender-invite-supplier-submit',
          })}
        </Button>,
      ]}>
      <div
        style={{
          padding: '14px 16px',
          marginBottom: 20,
          border: '1px solid #d6e4ff',
          borderRadius: 10,
          background:
            'linear-gradient(135deg, rgba(230,244,255,0.95), rgba(255,255,255,0.95))',
          boxShadow: '0 6px 18px rgba(24, 144, 255, 0.08)',
        }}>
        <div
          style={{
            marginBottom: 6,
            fontSize: 11,
            fontWeight: 700,
            color: '#1677ff',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
          {intl.formatMessage({ id: 'public-tender-header' })}
        </div>

        <div style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>
          {tenderTitle}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            marginTop: 10,
            fontSize: 12,
            color: '#595959',
          }}>
          {/*{globalSerial && (*/}
          {/*  <span>*/}
          {/*    {intl.formatMessage({ id: 'gs-short' })}: {globalSerial}*/}
          {/*  </span>*/}
          {/*)}*/}

          {tenderDeadline > 3601 && (
            <span>
              {intl.formatMessage({ id: 'deadline' })}:{' '}
              {formatDate(tenderDeadline, 'short')}
            </span>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 20, color: '#595959', lineHeight: 1.55 }}>
        {intl.formatMessage({
          id: 'public-tender-invite-supplier-modal-description',
        })}
      </div>

      <Form<PublicShareInviteFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmit}>
        <Form.Item
          name="supplierName"
          label={intl.formatMessage({
            id: 'public-tender-supplier-name',
          })}
          rules={[
            {
              required: true,
              whitespace: true,
              message: intl.formatMessage({
                id: 'public-tender-supplier-name-required',
              }),
            },
          ]}>
          <Input
            autoFocus
            prefix={<UserOutlined style={{ color: '#bfbfbf' }} rev={''} />}
            placeholder={intl.formatMessage({
              id: 'public-tender-supplier-name-placeholder',
            })}
          />
        </Form.Item>

        <Form.Item
          name="contactEmail"
          label={intl.formatMessage({
            id: 'public-tender-supplier-contact-email',
          })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'public-tender-supplier-contact-email-required',
              }),
            },
            {
              type: 'email',
              message: intl.formatMessage({
                id: 'public-tender-supplier-contact-email-invalid',
              }),
            },
          ]}>
          <Input
            prefix={<MailOutlined style={{ color: '#bfbfbf' }} rev={''} />}
            placeholder={intl.formatMessage({
              id: 'public-tender-supplier-contact-email-placeholder',
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
